import { useState, useMemo, useCallback, useEffect } from 'react'
import { Header } from './components/Header'
import { CartDrawer } from './components/CartDrawer'
import { InquiryDrawer } from './components/InquiryDrawer'
import { HomeView } from './views/HomeView'
import { CatalogView } from './views/CatalogView'
import { ProductDetailView } from './views/ProductDetailView'
import { AboutView } from './views/AboutView'
import { WhoWeAreView } from './views/WhoWeAreView'
import { ContactView } from './views/ContactView'
import { CategoriesView } from './views/CategoriesView'
import { Footer } from './components/Footer'

function App() {
  // View routing is managed in plain React state — no routing library.
  const [view, setView] = useState('home')
  const [segment, setSegment] = useState('retail')
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [catalogCategory, setCatalogCategory] = useState(null)

  const [cart, setCart] = useState([])
  const [inquiry, setInquiry] = useState([])
  const [cartOpen, setCartOpen] = useState(false)
  const [inquiryOpen, setInquiryOpen] = useState(false)

  // Persist basket state to localStorage so it survives refresh.
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('alpha_cart')
      const savedInquiry = localStorage.getItem('alpha_inquiry')
      if (savedCart) setCart(JSON.parse(savedCart))
      if (savedInquiry) setInquiry(JSON.parse(savedInquiry))
    } catch {
      // ignore parse errors
    }
  }, [])

  useEffect(() => {
    localStorage.setItem('alpha_cart', JSON.stringify(cart))
  }, [cart])

  useEffect(() => {
    localStorage.setItem('alpha_inquiry', JSON.stringify(inquiry))
  }, [inquiry])

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id)
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, qty: item.qty + qty } : item
        )
      }
      return [...prev, { product, qty }]
    })
    setCartOpen(true)
  }, [])

  const updateCartQty = useCallback((productId, qty) => {
    setCart((prev) =>
      prev.map((item) => (item.product.id === productId ? { ...item, qty } : item))
    )
  }, [])

  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId))
  }, [])

  const addToInquiry = useCallback((product, qty = product.moq || 1) => {
    setInquiry((prev) => {
      if (prev.some((item) => item.product.id === product.id)) return prev
      return [...prev, { product, qty }]
    })
    setInquiryOpen(true)
  }, [])

  const removeFromInquiry = useCallback((productId) => {
    setInquiry((prev) => prev.filter((item) => item.product.id !== productId))
  }, [])

  const submitInquiry = useCallback((payload) => {
    console.log('Inquiry submitted:', payload)
    // TODO: POST to backend /inquiries endpoint when API is ready.
    setInquiry([])
  }, [])

  const onSelectProduct = useCallback((product) => {
    setSelectedProduct(product)
    setView('product')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const onNavigate = useCallback((nextView) => {
    setView(nextView)
    setSelectedProduct(null)
    if (nextView !== 'catalog') {
      setCatalogCategory(null)
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const onCategorySelect = useCallback((category) => {
    setCatalogCategory(category)
    setView('catalog')
    setSelectedProduct(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const onAdd = useCallback(
    (product, qty) => {
      if (segment === 'retail') {
        addToCart(product, qty)
      } else {
        addToInquiry(product, qty)
      }
    },
    [segment, addToCart, addToInquiry]
  )

  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.qty, 0), [cart])
  const inquiryCount = inquiry.length

  return (
    <div className="min-h-screen bg-brand-cream">
      <Header
        segment={segment}
        onSegmentChange={(next) => {
          setSegment(next)
          if (view === 'product') setView('catalog')
        }}
        onNavigate={onNavigate}
        cartCount={cartCount}
        inquiryCount={inquiryCount}
        onOpenCart={() => setCartOpen(true)}
        onOpenInquiry={() => setInquiryOpen(true)}
      />

      {view === 'home' && (
        <HomeView
          segment={segment}
          onNavigate={onNavigate}
          onSegmentChange={setSegment}
          onSelectProduct={onSelectProduct}
          onAdd={onAdd}
        />
      )}

      {view === 'categories' && (
        <CategoriesView
          segment={segment}
          onNavigate={onNavigate}
          onSelectCategory={onCategorySelect}
        />
      )}

      {view === 'catalog' && (
        <CatalogView
          segment={segment}
          preselectedCategory={catalogCategory}
          onSelectProduct={onSelectProduct}
          onAdd={onAdd}
        />
      )}

      {view === 'product' && selectedProduct && (
        <ProductDetailView
          product={selectedProduct}
          segment={segment}
          onBack={() => onNavigate('catalog')}
          onAdd={onAdd}
        />
      )}

      {view === 'about' && <AboutView onNavigate={onNavigate} />}

      {view === 'who-we-are' && <WhoWeAreView />}

      {view === 'contact' && <ContactView />}

      <Footer onNavigate={onNavigate} />

      {cartOpen && (
        <CartDrawer
          items={cart}
          onClose={() => setCartOpen(false)}
          onUpdateQty={updateCartQty}
          onRemove={removeFromCart}
        />
      )}

      {inquiryOpen && (
        <InquiryDrawer
          items={inquiry}
          onClose={() => setInquiryOpen(false)}
          onRemove={removeFromInquiry}
          onSubmit={submitInquiry}
        />
      )}
    </div>
  )
}

export default App
